import React from 'react';
import { Link } from 'react-router-dom';
import '../../assets/css/CapabilityAccessBanner.css';

export type CapabilityAccessVariant = 'locked' | 'pending' | 'unlocked' | 'rejected';

interface CapabilityAccessBannerProps {
  variant: CapabilityAccessVariant;
  onRequest?: () => void;
  requesting?: boolean;
  rejectionComment?: string | null;
  /** Optional link CTA (e.g. jump to capability selection when unlocked). */
  ctaTo?: string;
  ctaLabel?: string;
  className?: string;
}

const VARIANT_COPY: Record<
  CapabilityAccessVariant,
  { pill: string; title: string; description: string; actionLabel: string }
> = {
  locked: {
    pill: 'Locked',
    title: 'Add more capabilities',
    description:
      'Additional capabilities are locked. Send a request to your advisor to unlock more areas for assessment.',
    actionLabel: 'Request to add capabilities',
  },
  pending: {
    pill: 'Pending review',
    title: 'Request awaiting approval',
    description:
      "Your advisor is reviewing your request. New capabilities will unlock automatically once it's approved.",
    actionLabel: 'Request pending',
  },
  unlocked: {
    pill: 'Unlocked',
    title: 'Capabilities unlocked',
    description:
      'Your request was approved. You can now select and assess additional capabilities.',
    actionLabel: 'Select capabilities',
  },
  rejected: {
    pill: 'Declined',
    title: 'Request declined',
    description: "Your request to add capabilities wasn't approved.",
    actionLabel: 'Re-request capabilities',
  },
};

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="4" y="10.5" width="16" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="12" cy="15.5" r="1.4" fill="currentColor" />
  </svg>
);

const UnlockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="4" y="10.5" width="16" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M8 10.5V7.5a4 4 0 0 1 7.5-1.9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="12" cy="15.5" r="1.4" fill="currentColor" />
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12 8v4.2l2.8 1.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12 8v4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="12" cy="15.6" r="1" fill="currentColor" />
  </svg>
);

const ICONS: Record<CapabilityAccessVariant, React.FC> = {
  locked: LockIcon,
  pending: ClockIcon,
  unlocked: UnlockIcon,
  rejected: AlertIcon,
};

const CapabilityAccessBanner: React.FC<CapabilityAccessBannerProps> = ({
  variant,
  onRequest,
  requesting = false,
  rejectionComment = null,
  ctaTo,
  ctaLabel,
  className = '',
}) => {
  const copy = VARIANT_COPY[variant];
  const Icon = ICONS[variant];

  const description =
    variant === 'rejected' && rejectionComment
      ? `${copy.description} Reason: ${rejectionComment}`
      : copy.description;

  const canRequest = variant === 'locked' || variant === 'rejected';

  return (
    <div
      className={`cap-access cap-access--${variant}${className ? ` ${className}` : ''}`}
      role="status"
    >
      <span className="cap-access__icon" aria-hidden="true">
        <Icon />
      </span>

      <div className="cap-access__body">
        <div className="cap-access__heading">
          <span className="cap-access__title">{copy.title}</span>
          <span className="cap-access__pill">{copy.pill}</span>
        </div>
        <p className="cap-access__text">{description}</p>
      </div>

      <div className="cap-access__action">
        {variant === 'unlocked' && ctaTo ? (
          <Link to={ctaTo} className="btn-dashboard-primary cap-access__btn">
            {ctaLabel || copy.actionLabel}
          </Link>
        ) : variant === 'pending' ? (
          <button type="button" className="btn-dashboard-primary cap-access__btn" disabled>
            {copy.actionLabel}
          </button>
        ) : canRequest && onRequest ? (
          <button
            type="button"
            className="btn-dashboard-primary cap-access__btn"
            onClick={onRequest}
            disabled={requesting}
          >
            {requesting ? 'Sending…' : copy.actionLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default CapabilityAccessBanner;
